// ─── Il calendario: prenotazioni, chi è in campo, e la finestra che si guarda ─
//
// 🎯 È metà dell'applicativo (§ 06, Fase 4), ed è la schermata su cui la
// segreteria sta tutto il giorno.
//
// 🕰️⭐⭐ LA DECISIONE CHE GOVERNA TUTTO QUESTO FILE: **il calendario parla
// LOCALE, e a convertire è POSTGRES.**
//
// Il database tiene gli istanti (`timestamptz`), che è la verità; ma una fascia
// prenotabile è «il lunedì dalle 09:00», una chiusura è «il 15 agosto», e un
// operatore guarda una griglia di ore di Roma. ⇒ Ogni cosa che serve a
// disegnare la griglia esce di qui **già in ora locale** — `date` più `time`,
// oppure minuti dalla mezzanotte — e chi la disegna fa aritmetica di stringhe e
// di interi, mai di fusi.
//
// ⚖️ Perché la conversione sta in Postgres e non qui: Postgres ha il database
// dei fusi e conosce l'ora legale; un `new Date()` in JavaScript la indovina
// dal fuso del processo, che dentro un container è `UTC`. 📌 *Una conversione
// fatta a mano non sbaglia mai in prova: sbaglia l'ultima domenica di marzo.*
//
// ⚠️ Gli istanti viaggiano lo stesso (`startsAt`, `endsAt`), perché sono ciò
// che è scritto nel database e perché un client che ne avesse bisogno non deve
// ricostruirli. Ma la griglia non li guarda mai.
import { z } from 'zod';
import { campoSchema, dataSchema, fasciaSchema, maestroSchema, oraSchema, orarioCircoloSchema, } from './circolo.js';
import { cassaDellaPrenotazioneSchema, statoIncasso } from './cassa.js';
import { livelloCorsoSchema } from './tariffe-lezioni.js';
import { caparraDallaSegreteriaSchema, caparraDellaSegreteriaSchema, caparraInBreveSchema, rispostaConfermaSchema, } from './caparra.js';
/**
 * ⚠️ SONO SEI, e i tipi del LISTINO sono QUATTRO (`TIPI_PRENOTAZIONE`): la
 * `manutenzione` occupa un campo e non si paga, quindi esiste in calendario e
 * non in listino. Le due liste si somigliano e non sono la stessa: chi le
 * unificasse si troverebbe a cercare il prezzo di una manutenzione.
 *
 * 🆕 **L'`allenamento`** (voce 19c, 24/09/2026) è una lezione da un'ora e mezza:
 * il suo prezzo sta nel «Listino prezzi lezioni», come quello della lezione.
 * 🗣️ **E l'ORDINE È QUELLO DELLA TENDINA**, detto da lui guardando il mockup:
 * *«sotto lezione va bene allenamento e poi sotto allenamento metterei corso,
 * poi manutenzione e poi torneo»*. La tendina scorre questa lista così com'è.
 */
export const TIPI_BOOKING = ['partita', 'lezione', 'allenamento', 'corso', 'manutenzione', 'torneo'];
export const tipoBookingSchema = z.enum(TIPI_BOOKING);
/**
 * Quante persone ci stanno, per tipo (§ 02.2, regola ⑤).
 *
 * 🔄⭐⭐ **SEI PER LEZIONE, CORSO E TORNEO — 17/09/2026, richiesta sua:**
 * 🗣️ *«Mi dai la possibilità per una lezione, un corso e un torneo di inserire
 *   fino a sei persone in un campo?»*
 *
 * ⚖️ **La PARTITA resta a quattro, e non è una dimenticanza.** In un campo da
 * padel una partita si gioca in quattro: non è una capienza, è la regola del
 * gioco. Una lezione, un corso e un torneo invece sono momenti in cui sul campo
 * ci sta chi il circolo decide — ed è la cosa che lui ha chiesto di poter fare.
 *
 * 🔒 **Il tetto assoluto lo impone il DATABASE** (`position between 1 and 8`
 * dal 24/09/2026, migrazione `1758500000000_prenotare_col_listino`; prima 6). Alzare un numero qui sopra
 * quel tetto non allargherebbe niente: farebbe passare la settima persona da
 * tutte le regole per poi farla morire contro un vincolo, cioè un errore che
 * non si capisce al posto di una frase che si capisce. ⇒ `POSTI_MASSIMI` qui
 * sotto è ciò che gli schemi guardano, e nasce da questa tabella.
 *
 * ⚠️ **Non è il numero su cui si divide il prezzo**, e i due si somigliano
 * abbastanza da confondersi: quello sta in `prezzo.ts` ed è fermo a quattro
 * apposta (⇒ `GIOCATORI_PER_QUOTA`).
 *
 * 🔄 **Lezione e allenamento a QUATTRO, corso a OTTO — 24/09/2026 (voce 19c).**
 * Il «Listino prezzi lezioni» ha un prezzo da una a quattro persone e non oltre:
 * una lezione da cinque non avrebbe un prezzo da nessuna parte. Il corso sale a
 * otto, detto da lui: *«un corso può arrivare fino a un massimo di 8
 * partecipanti»*. ⇒ Il tetto del database sale a **otto** con loro
 * (migrazione `1758500000000_prenotare_col_listino`).
 *
 * La manutenzione è a **0** perché non è gente in campo, è un campo chiuso.
 */
export const CAPIENZA = {
    partita: 4,
    lezione: 4,
    allenamento: 4,
    torneo: 6,
    corso: 8,
    manutenzione: 0,
};
/**
 * ⭐ Il posto più alto che può esistere, **contato** dalla tabella qui sopra
 * invece che riscritto.
 *
 * 🔒 Lo guardano gli schemi (`players`, `position`), che senza di lui
 * porterebbero un secondo numero da tenere allineato a mano: il giorno in cui
 * un tipo salisse a otto, uno schema fermo a sei rifiuterebbe una persona che
 * la capienza ammette — e lo direbbe con un errore di validazione, cioè nel
 * posto dove nessuno va a cercare una regola di capienza.
 * ⚠️ Deve restare **≤ 8**, o il muro vero (il database) lo ferma dopo.
 */
export const POSTI_MASSIMI = Math.max(...Object.values(CAPIENZA));
/**
 * ⭐⭐ **SE SU QUESTA PRENOTAZIONE PUÒ PASSARE DEL DENARO** — 18/09/2026,
 * richiesta sua: *«fammi vedere la barretta anche sulle partite senza
 * importo»*.
 *
 * ⚖️ Serve a distinguere le DUE cose che prima la barretta confondeva in un
 * silenzio solo:
 * · una **partita senza importo** è una cosa che qualcuno deve ancora
 *   sistemare ⇒ si vede, e si vede che è da sistemare;
 * · una **manutenzione** è un campo **chiuso** — non è gente che deve pagare,
 *   e non lo sarà mai (`CAPIENZA` 0, ⇒ *«occupa un campo e non si paga»*).
 *   Darle una barretta vorrebbe dire mettere un segno di cassa su una cosa che
 *   la cassa non tocca.
 *
 * 🚨 Si legge dalla **capienza**, non da un secondo elenco di tipi: chi domani
 * aggiungesse un tipo lo dichiarerebbe in un posto solo. Un tipo che non può
 * avere nessuno in campo non può avere nessuno che paga.
 */
export function puoAvereDenaro(tipo) {
    return CAPIENZA[tipo] > 0;
}
/**
 * ⭐⭐ **IL CAMPO È SALDATO**: non c'è più niente da incassare su questa
 * prenotazione — 18/09/2026, ed è ciò che sul calendario dipinge di verde
 * l'intero blocchetto.
 *
 * ⚖️ **Sta in `shared` e non nella schermata** perché è una regola di dominio,
 * non un dettaglio di disegno: la stessa domanda se la porranno un elenco, un
 * foglio di fine giornata o un avviso, e tre risposte scritte in tre posti
 * divergono al primo caso storto.
 *
 * ⛔ **`puoAvereDenaro` viene PRIMA dello stato, e non è ridondante**: una
 * manutenzione ha zero a carico e zero incassato, cioè esattamente la forma di
 * una cosa «tutta pagata». Senza questo controllo ogni campo chiuso per
 * manutenzione diventerebbe verde — e il verde smetterebbe di voler dire che
 * qualcuno ha pagato.
 */
export function campoSaldato(tipo, quote, incassatoCents) {
    return puoAvereDenaro(tipo) && statoIncasso(quote, incassatoCents) === 'incassata';
}
/**
 * ⭐⭐ **CHI HA UN MAESTRO, E CHI NON CE L'HA** — 17/09/2026, richiesta sua.
 *
 * 🗣️ *«quando seleziono manutenzione, se per sbaglio è inserito un maestro non
 *   deve apparire sul calendario; lo stesso vale per la partita e per il
 *   torneo»* — e, chiesto perché contraddiceva la richiesta del giorno prima,
 *   **«il corso lo mostra»**.
 *
 * | tipo | il maestro |
 * |---|---|
 * | **lezione · corso** | è obbligatorio, e si vede sul calendario |
 * | **partita · torneo · manutenzione** | non c'entra: non si chiede e non si mostra |
 *
 * ⚖️ **È un `Record` e non una funzione con due `||`**, per la stessa ragione di
 * `CAPIENZA` qui sopra: il giorno in cui nasce un sesto tipo, il compilatore
 * **pretende** che qualcuno decida se ha un maestro. Una funzione scritta a mano
 * risponderebbe `false` in silenzio, e nessuno saprebbe che non gliel'ha chiesto
 * nessuno.
 *
 * 🔒 E sta in `shared` perché la stessa frase serve in **quattro** posti che
 * devono dire la stessa cosa: il blocchetto del calendario (cosa si vede), la
 * scheda (cosa si chiede), il piede della scheda (cosa manca per salvare) e il
 * server (cosa si scrive in colonna). Quattro copie a mano divergono.
 */
export const VUOLE_MAESTRO = {
    partita: false,
    lezione: true,
    allenamento: true,
    corso: true,
    torneo: false,
    manutenzione: false,
};
export function vuoleMaestro(tipo) {
    return VUOLE_MAESTRO[tipo];
}
export const CADENZE = [
    'giornaliera',
    'ogni2giorni',
    'ogni3giorni',
    'settimanale',
    'quindicinale',
    'ogni3settimane',
    'ogni4settimane',
    'mensile',
];
export const cadenzaSchema = z.enum(CADENZE);
/**
 * 🔒 **Le parole delle cadenze non si RINOMINANO mai, si AGGIUNGONO soltanto.**
 * Le serie già create puntano a questa parola: cambiarla vorrebbe dire un corso
 * che da domani si srotola in un altro modo, senza che nessun errore lo dica.
 */
export const CADENZA = {
    giornaliera: { famiglia: 'giorni', passo: 1, etichetta: 'ogni giorno' },
    ogni2giorni: { famiglia: 'giorni', passo: 2, etichetta: 'ogni due giorni' },
    ogni3giorni: { famiglia: 'giorni', passo: 3, etichetta: 'ogni tre giorni' },
    settimanale: { famiglia: 'settimane', passo: 1, etichetta: 'ogni settimana' },
    quindicinale: { famiglia: 'settimane', passo: 2, etichetta: 'ogni due settimane' },
    ogni3settimane: { famiglia: 'settimane', passo: 3, etichetta: 'ogni tre settimane' },
    ogni4settimane: { famiglia: 'settimane', passo: 4, etichetta: 'ogni quattro settimane' },
    mensile: { famiglia: 'mese', passo: 1, etichetta: 'ogni mese' },
};
export const ETICHETTE_CADENZA = Object.fromEntries(CADENZE.map((c) => [c, CADENZA[c].etichetta]));
/**
 * ⭐ **Lunedì è lo zero**, e non è il default di JavaScript (per lui lo zero è
 * la domenica). Una settimana che comincia di domenica metterebbe il weekend a
 * cavallo di due righe, e il circolo la settimana la conta dal lunedì.
 */
export const GIORNI_SETTIMANA = [
    'lunedì',
    'martedì',
    'mercoledì',
    'giovedì',
    'venerdì',
    'sabato',
    'domenica',
];
export const SIGLE_SETTIMANA = ['lun', 'mar', 'mer', 'gio', 'ven', 'sab', 'dom'];
/** `2026-09-17` → 3 (giovedì). Zero = lunedì. */
export function indiceDelGiorno(data) {
    return (new Date(`${data}T00:00:00Z`).getUTCDay() + 6) % 7;
}
/**
 * 🔒 **IL TETTO DI UNA SERIE — due limiti, e servono a due cose diverse.**
 *
 * ⛔ `VOLTE_MASSIME` protegge dalla **quantità**: ogni volta è una prenotazione
 * vera, con la sua gente e **un avviso per ciascun giocatore**. Cento volte per
 * quattro sono quattrocento messaggi da un clic solo, ed è quello il costo che
 * si paga fuori — non le righe nel database.
 * ⚖️ `ORIZZONTE_GIORNI` protegge dall'**orizzonte**: un anno è il tempo entro
 * cui il circolo sa cosa farà. Oltre non si sta prenotando, si sta indovinando,
 * e i campi restano tenuti per qualcuno che magari non rinnova.
 *
 * 🔄 **120 e non più 53, e il perché del 53 è caduto con le cadenze nuove.**
 * 53 era *«un anno di settimane»* — un numero che valeva **solo** per la
 * settimanale. Un corso lun·mer·ven da ottobre a maggio sono **104** volte, e
 * con il tetto vecchio si sarebbe fermato a febbraio: un limite tarato su una
 * cadenza sola diventa un difetto su tutte le altre.
 * 📌 *Un numero giusto per un caso e sbagliato per gli altri sei non è un
 *    limite: è il ricordo di quando il caso era uno solo.*
 */
export const VOLTE_MASSIME = 120;
/** Oltre un anno dal primo giorno non si va, qualunque sia la cadenza. */
export const ORIZZONTE_GIORNI = 366;
/**
 * ⭐ `2026-09-17` più `n` giorni. Aritmetica di calendario, nessun fuso di
 * mezzo: si passa per l'UTC di mezzanotte apposta, perché è l'unico istante che
 * l'ora legale non sposta.
 *
 * 🚨 Sta in `shared` perché la stessa riga esisteva già in **tre** copie —
 * il calendario del web, le rotte dell'api e le regole dell'assistente — e tre
 * copie della stessa aritmetica sono tre posti dove correggere un fuso.
 */
export function piuGiorni(data, n) {
    return new Date(Date.parse(`${data}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
}
function inIso(anno, mese, giorno) {
    return new Date(Date.UTC(anno, mese, giorno)).toISOString().slice(0, 10);
}
/** Quanti giorni ha quel mese. `mese` è 0-11, come in JavaScript. */
function quantiGiorniHa(anno, mese) {
    return new Date(Date.UTC(anno, mese + 1, 0)).getUTCDate();
}
/**
 * ⭐⭐ **CHE POSTO OCCUPA NEL MESE — `1..4`, oppure `-1` che vuol dire «l'ULTIMO».**
 *
 * ⚖️ **Perché «l'ultimo» è un caso a sé e non il quinto.** Un mese ha quattro o
 * cinque giovedì a seconda di com'è fatto: chiamare «il quinto giovedì» una
 * regola vorrebbe dire **saltare i mesi che non ce l'hanno**, cioè una serie
 * mensile con dei buchi dentro. ⇒ Quando il giorno di partenza è l'ultimo del
 * suo tipo nel mese, la regola diventa *«l'ultimo giovedì»* — che ogni mese
 * esiste, sempre, e che per di più è il modo in cui la gente lo dice davvero.
 *
 * 📌 *Una regola di calendario che può non avere risposta in certi mesi non è
 *    una regola: è una regola più un'eccezione che nessuno ha scritto.*
 */
export function ordinaleNelMese(data) {
    const giorno = Number(data.slice(8, 10));
    const anno = Number(data.slice(0, 4));
    const mese = Number(data.slice(5, 7)) - 1;
    if (giorno + 7 > quantiGiorniHa(anno, mese))
        return -1;
    return Math.floor((giorno - 1) / 7) + 1;
}
const ORDINALI = ['il primo', 'il secondo', 'il terzo', 'il quarto'];
/** `2026-09-24` → «l'ultimo giovedì». È ciò che il pannello mostra per non far indovinare. */
export function etichettaMensile(dal) {
    const nome = GIORNI_SETTIMANA[indiceDelGiorno(dal)];
    const ord = ordinaleNelMese(dal);
    return ord === -1 ? `l’ultimo ${nome}` : `${ORDINALI[ord - 1]} ${nome}`;
}
/** Ordinati, senza doppioni, e mai vuoti: senza scelta vale il giorno di partenza. */
export function giorniScelti(regola, dal) {
    const scelti = [...new Set((regola.giorni ?? []).filter((g) => g >= 0 && g <= 6))].sort((a, b) => a - b);
    return scelti.length === 0 ? [indiceDelGiorno(dal)] : scelti;
}
/**
 * ⭐⭐ **I GIORNI DI UNA SERIE, dal primo compreso fino all'ultimo che ci sta.**
 *
 * ⚖️ **Il primo giorno fa parte della serie**, e non è un dettaglio di conteggio:
 * chi prenota il giovedì e dice «ogni settimana fino a dicembre» sta prenotando
 * *anche* quel giovedì. Farlo nascere fuori dalla serie vorrebbe dire che
 * annullando «tutte» ne resterebbe una — proprio quella che si stava guardando.
 *
 * 🚨 **E nella famiglia `settimane` il primo giorno entra sempre, anche quando
 * non è fra quelli spuntati.** Chi apre il pannello su un martedì e sceglie
 * «lunedì e mercoledì» sta comunque prenotando **quel martedì**: è la
 * prenotazione che ha davanti, e farla sparire perché non rientra nella regola
 * sarebbe un gesto che cancella ciò che si stava facendo.
 *
 * ⛔ `fino` **prima** del primo giorno rende un elenco di uno: il giorno stesso.
 * Non è un errore da rifiutare — è una serie che dura una volta sola, e il
 * risultato che chi chiede voleva (quella prenotazione) c'è lo stesso.
 *
 * 🔒 Oltre `VOLTE_MASSIME` **o** oltre `ORIZZONTE_GIORNI` si TAGLIA, e chi
 * chiama lo dice a chi guarda: rendere un elenco più corto in silenzio sarebbe
 * far credere che il circolo sia prenotato fino a giugno quando lo è fino a
 * gennaio.
 */
export function giorniDellaSerie(dal, regola, fino) {
    const orizzonte = piuGiorni(dal, ORIZZONTE_GIORNI);
    const limite = fino < orizzonte ? fino : orizzonte;
    const def = CADENZA[regola.cadenza];
    const giorni = [];
    if (def.famiglia === 'giorni') {
        let g = dal;
        while (g <= limite && giorni.length < VOLTE_MASSIME) {
            giorni.push(g);
            g = piuGiorni(g, def.passo);
        }
    }
    else if (def.famiglia === 'settimane') {
        const scelti = giorniScelti(regola, dal);
        const lunedi = piuGiorni(dal, -indiceDelGiorno(dal));
        for (let k = 0; giorni.length < VOLTE_MASSIME; k += def.passo) {
            const inizio = piuGiorni(lunedi, k * 7);
            if (inizio > limite)
                break;
            for (const gs of scelti) {
                const g = piuGiorni(inizio, gs);
                if (g < dal || g > limite)
                    continue;
                giorni.push(g);
                if (giorni.length >= VOLTE_MASSIME)
                    break;
            }
        }
        /*
         * ⭐ Il giorno di partenza entra comunque, e in ordine: vedi sopra — è la
         * prenotazione che si sta facendo, non un giorno che la regola ha prodotto.
         */
        if (!giorni.includes(dal)) {
            giorni.push(dal);
            giorni.sort();
            if (giorni.length > VOLTE_MASSIME)
                giorni.length = VOLTE_MASSIME;
        }
    }
    else {
        const gs = indiceDelGiorno(dal);
        const ord = ordinaleNelMese(dal);
        let anno = Number(dal.slice(0, 4));
        let mese = Number(dal.slice(5, 7)) - 1;
        while (giorni.length < VOLTE_MASSIME && inIso(anno, mese, 1) <= limite) {
            const quanti = quantiGiorniHa(anno, mese);
            const primoDelTipo = 1 + ((gs - indiceDelGiorno(inIso(anno, mese, 1)) + 7) % 7);
            let quale = ord === -1 ? primoDelTipo : primoDelTipo + (ord - 1) * 7;
            if (ord === -1)
                while (quale + 7 <= quanti)
                    quale += 7;
            if (quale <= quanti) {
                const g = inIso(anno, mese, quale);
                if (g >= dal && g <= limite)
                    giorni.push(g);
            }
            mese += 1;
            if (mese > 11) {
                mese = 0;
                anno += 1;
            }
        }
    }
    return giorni.length === 0 ? [dal] : giorni;
}
/**
 * ⭐⭐ **A CHI SI APPLICA UNA MODIFICA, quando la prenotazione è di una serie.**
 *
 * 🗣️ *«e logicamente di modificare o cancellare una periodicità»* — e sono
 * **tre** cose diverse, non due:
 * · `una` — solo questa volta (il giovedì che si salta, la lezione spostata di
 *   un'ora per una volta sola);
 * · `daQui` — da questa in avanti (il corso che dalle 18:00 passa alle 19:00);
 * · `tutte` — anche quelle già passate (una correzione: il maestro sbagliato,
 *   il tipo sbagliato).
 *
 * ⚠️ **`una` è il valore predefinito, e deve esserlo.** Chi non dice niente sta
 * toccando la prenotazione che ha davanti: indovinare «tutte» vorrebbe dire che
 * un trascinamento sul calendario sposta sei mesi di corsi senza che nessuno
 * l'abbia chiesto. 📌 *Il valore predefinito di un gesto distruttivo è sempre
 * quello che fa meno danno, anche quando è quello che serve meno spesso.*
 */
export const AMBITI_SERIE = ['una', 'daQui', 'tutte'];
export const ambitoSerieSchema = z.enum(AMBITI_SERIE);
export const ETICHETTE_AMBITO = {
    una: 'solo questa',
    daQui: 'da questa in poi',
    tutte: 'tutte',
};
/**
 * ⭐⭐ **COME SI RIPETE, detto in italiano** — «di giovedì», «il lun · mer
 * · ven», «l'ultimo giovedì del mese», «una ogni due giorni».
 *
 * 🚨 Sta in `shared` e non nel pannello perché lo dicono in **tre** posti — la
 * riga viva mentre si sceglie, la scheda di una prenotazione già fatta e il
 * suggerimento sul blocchetto del calendario — e tre frasi scritte a mano sono
 * tre occasioni perché il calendario e la scheda descrivano la stessa serie in
 * due modi diversi.
 */
export function comeSiRipete(dal, regola) {
    const def = CADENZA[regola.cadenza];
    if (def.famiglia === 'mese')
        return `${etichettaMensile(dal)} del mese`;
    if (def.famiglia === 'giorni')
        return def.passo === 1 ? 'una al giorno' : `una ogni ${def.passo === 2 ? 'due' : 'tre'} giorni`;
    const scelti = giorniScelti(regola, dal);
    return scelti.length === 1
        ? // 🔄 27/09/2026: era «tutte il giovedì», che nella scheda si leggeva «si ripete ogni settimana, tutte il giovedì».
            `di ${GIORNI_SETTIMANA[scelti[0]]}`
        : `il ${scelti.map((g) => SIGLE_SETTIMANA[g]).join(' · ')}`;
}
/**
 * La serie a cui una prenotazione appartiene, per quel poco che serve a chi la
 * guarda: la cadenza, fin dove arriva, e **quante ce ne sono ancora**.
 */
export const serieDellaPrenotazioneSchema = z.object({
    id: z.uuid(),
    cadenza: cadenzaSchema,
    /**
     * ⭐ **Il giorno da cui la serie è nata**, e viaggia con lei perché è l'unica
     * cosa che sa dire *«l'ultimo giovedì»* invece di *«il quarto»*: la regola
     * mensile è una posizione nel mese, e la posizione la dà il primo giorno.
     * 🚨 Non è «la prima prenotazione viva»: quella si può annullare, e con lei se
     * ne andrebbe la regola di srotolamento di tutte le altre.
     */
    dal: dataSchema,
    /** Famiglia `settimane`: quali giorni. Nullo per tutte le altre cadenze. */
    giorni: z.array(z.number().int().min(0).max(6)).nullable(),
    fino: dataSchema,
    /** Quante prenotazioni vive ha la serie, questa compresa. */
    quante: z.number().int(),
    /** Che numero è questa, contando dalla prima: «la 3ª di 12». */
    quale: z.number().int(),
    /**
     * 🆕 Il livello del corso (voce 19c): dice quale quota del «Listino prezzi
     * lezioni» paga ogni iscritto. Nullo sulle serie che non sono corsi, e sui
     * corsi aperti dall'app dei maestri finché lì non si sceglie.
     */
    livello: livelloCorsoSchema.nullable().default(null),
    /** 🔁 27/09/2026 — l'ha chiesta un socio dal bot (il campo fisso dal bot). */
    dalBot: z.boolean().default(false),
    /**
     * 🔁 27/09/2026 (voce 47) — l'avviso del rinnovo: il socio è stato avvisato, ha rinnovato, o ha
     * detto «No, grazie» (e allora dopo l'ultima partita quel campo è libero). Nullo prima dell'avviso.
     */
    rinnovo: z.enum(['avvisato', 'rinnovato', 'non_rinnova']).nullable().default(null),
});
export const STATI_PRENOTAZIONE = ['confirmed', 'cancelled'];
export const statoPrenotazioneSchema = z.enum(STATI_PRENOTAZIONE);
// ── Aritmetica dell'orologio, senza fusi ────────────────────────────────────
/** `14:30` → 870. L'unità di tutta la griglia sono i minuti dalla mezzanotte. */
export function minutiDaOra(ora) {
    const [h, m] = ora.split(':');
    return Number(h) * 60 + Number(m);
}
/** Quanti minuti stanno in un giorno: la soglia oltre la quale l'orologio mente. */
const MINUTI_AL_GIORNO = 24 * 60;
/**
 * 870 → `14:30`.
 *
 * ⚠️ Oltre le 24 ore **non gira a zero**: torna `24:00`, `25:30`… Una griglia
 * che si chiude a mezzanotte deve poterlo dire, e un `00:00` al posto di
 * `24:00` la farebbe sembrare lunga zero minuti.
 */
export function oraDaMinuti(min) {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
/** `19:30` + 90 → `21:00`. Nessun fuso, nessuna data: solo l'orologio. */
export function oraPiuMinuti(ora, minuti) {
    return oraDaMinuti(minutiDaOra(ora) + minuti);
}
/**
 * ⭐⭐ **COME SI DICE A VOCE UN'ORA CHE PUÒ SFONDARE LA MEZZANOTTE.**
 *
 * 🚨 Nasce da un difetto misurato sull'app viva il 18/09/2026: stirando una
 * partita delle 20:00 fin giù in fondo alla giornata, il rifiuto diceva
 * *«finirebbe alle **29:15**»*. Le 29:15 non esistono, e chi legge si ferma su
 * quella parola invece che sul motivo del rifiuto.
 * ⚖️ **`oraDaMinuti` resta com'è e non si tocca**: là il `25:30` serve, ed è
 *   documentato sopra — una fascia che finisce a mezzanotte deve poterlo dire
 *   senza sembrare lunga zero minuti. Quella è la lingua della **griglia**;
 *   questa è la lingua di chi **legge una frase**, e sono due cose diverse.
 * ⇒ Rende la locuzione intera, preposizione compresa, perché *«alle
 *   mezzanotte»* non si dice e un chiamante che incollasse «alle » davanti
 *   sbaglierebbe proprio il caso più comune.
 * 📌 *Un numero giusto dentro una frase sbagliata non è un dettaglio di stile:
 *    è il punto in cui chi legge smette di credere al resto della frase.*
 */
export function alleOre(minuti) {
    if (minuti === MINUTI_AL_GIORNO)
        return 'a mezzanotte';
    if (minuti > MINUTI_AL_GIORNO)
        return `alle ${oraDaMinuti(minuti - MINUTI_AL_GIORNO)} del giorno dopo`;
    return `alle ${oraDaMinuti(minuti)}`;
}
// ── Chi è in campo ──────────────────────────────────────────────────────────
export const giocatoreSchema = z.object({
    id: z.uuid(),
    position: z.number().int(),
    /** Nullo = ospite. Un nome che non si risolve resta un ospite: non si inventa un socio. */
    memberId: z.uuid().nullable(),
    guestName: z.string().nullable(),
    /** Il nome da mostrare: quello del socio se c'è, altrimenti quello dell'ospite. */
    displayName: z.string(),
    /**
     * ⭐ Lo stesso nome col cognome puntato — `Lidia C.` — per il blocchetto del
     * calendario quando lo spazio non basta (⇒ `nomeAccorciato`).
     *
     * ⚖️ **Lo calcola il SERVER e non il browser**, e non è un capriccio: per un
     * socio nome e cognome sono **due colonne**, mentre a valle arriverebbe una
     * stringa sola da spaccare a indovinare — e «Anna Maria De Rossi» si
     * spaccherebbe nel punto sbagliato. Per un ospite, che è testo libero, il
     * ripiego di `spaccaNome` è tutto quello che c'è, e allora è dichiarato.
     * 📌 *Un dato che nasce diviso non si ricompone per poi ridividerlo a
     *    naso.*
     */
    shortName: z.string(),
    /**
     * 💶 NON È UN PAGAMENTO: è quanto quella persona DEVE. Si riscrive quante
     * volte si vuole, non genera nessuna riga di cassa (§ 01.5).
     */
    amountDueCents: z.number().int(),
    /**
     * 💶⭐⭐ Quanto è entrato in cassa **PER QUESTA PERSONA**, storni compresi:
     * un incasso e il suo storno valgono zero, com'è giusto.
     *
     * 🗣️ Nasce il 18/09/2026 da una cosa vista da lui: *«la prenotazione dentro
     * il calendario deve cambiare colore, perché manca un pagamento»*.
     *
     * ⚖️ **Viaggia col calendario, e non è un di più**: senza, il blocchetto sa
     * solo due somme — quanto si deve in tutto e quanto è entrato in tutto — e
     * quelle due possono pareggiare mentre **una persona non ha pagato**
     * (⇒ `statoIncasso`, dove sta il difetto misurato). Il verde si decide
     * persona per persona, quindi la persona deve portarsi dietro il suo conto.
     *
     * ⚠️ I soldi di chi è stato TOLTO dalla partita non sono di nessuno: restano
     * nel totale della prenotazione — sono in cassa davvero — e non coprono più
     * nessun giocatore. È la differenza che fa cadere la regola vecchia.
     */
    incassatoCents: z.number().int(),
    /**
     * 🚨⭐⭐ **NON C'ERA, e deve lo stesso.** 🗣️ *«Il corso si paga al mese»*
     * (19/09) ⇒ si paga il **posto**, non la presenza: una volta segnato
     * assente, la riga resta e `amountDueCents` con lei.
     *
     * ⚖️ **Viaggia fino alla segreteria apposta, e non è un di più**: è lei che
     * incassa, e senza questo campo chi batte la cassa vede in campo una persona
     * che non c'era, senza niente che glielo dica. 📌 *Un numero che compare solo
     * su una delle due schermate non è un dettaglio di presentazione: è una
     * delle due che sbaglia.*
     *
     * ⛔ **Lo SEGNA solo il maestro** (⇒ `PATCH /maestro/lezioni/:id/allievi/:id`):
     * è l'unico che sa chi c'era. Di qua si legge.
     */
    assente: z.boolean(),
    /**
     * ⭐ È fra gli **iscritti alla serie** di questa prenotazione — falso su
     * tutto ciò che una serie non ce l'ha.
     *
     * ⚖️ Serve a scegliere il **gesto**, mai il prezzo: su una volta di corso un
     * iscritto si segna *assente*, mentre chi è stato aggiunto a mano a quella
     * sola volta si *toglie*. Senza questo campo la schermata mostrerebbe un
     * bottone solo, e su metà delle righe sarebbe quello sbagliato — segnare
     * assente un non-iscritto lo lascerebbe a pagare un posto che non è suo.
     */
    iscritto: z.boolean(),
    /**
     * ⏱️ **Ha avvisato che arriva in ritardo** (27/09/2026, mockup «Scrivi alla
     * Segreteria» ④): l'ha detto all'assistente, e i compagni lo sanno. La
     * Segreteria non riceve niente; nella scheda lo vede prima di segnare
     * un'assenza. `at` è l'ora di Roma dell'avviso («17:52»).
     */
    ritardo: z.object({ minuti: z.number().int().nullable(), at: z.string() }).nullable().default(null),
});
// ── La prenotazione ─────────────────────────────────────────────────────────
export const prenotazioneSchema = z.object({
    id: z.uuid(),
    courtId: z.uuid(),
    /** L'istante vero, com'è scritto nel database. La griglia non lo guarda. */
    startsAt: z.string(),
    endsAt: z.string(),
    /** Il giorno e l'ora **di Roma**, calcolati da Postgres: questi li guarda la griglia. */
    date: dataSchema,
    time: oraSchema,
    durationMinutes: z.number().int(),
    type: tipoBookingSchema,
    title: z.string().nullable(),
    instructorId: z.uuid().nullable(),
    instructorName: z.string().nullable(),
    /** Il maestro col cognome puntato, per il blocchetto stretto. */
    instructorShortName: z.string().nullable(),
    notes: z.string().nullable(),
    status: statoPrenotazioneSchema,
    cancellationReason: z.string().nullable(),
    players: z.array(giocatoreSchema),
    /** La somma degli importi a carico: comoda in griglia, e non è un incasso. */
    totalDueCents: z.number().int(),
    /**
     * 💶 Quanto è GIÀ ENTRATO in cassa su questa prenotazione, storni compresi.
     *
     * 🗣️ Viaggia col calendario dal 17/09/2026, per una richiesta sua: *«sarebbe
     * utile a colpo d'occhio riuscire a distinguere una prenotazione pagata da
     * una ancora da pagare»*.
     *
     * ⚖️ **Col calendario e non a parte**, per la stessa ragione dei giocatori e
     * della serie: il blocchetto deve poterlo dire **mentre lo si guarda**. Una
     * domanda per prenotazione sarebbero duecento domande per una giornata
     * piena, cioè la differenza fra una griglia che si apre e una che si aspetta.
     *
     * ⚠️ Somma nuda: una coppia incasso+storno vale **zero** da sé, e il
     * blocchetto torna a dire «da incassare» — che è la verità.
     */
    incassatoCents: z.number().int(),
    /**
     * 🔁 **Nulla se questa prenotazione sta per conto suo.**
     *
     * ⚖️ Viaggia col calendario e non si chiede a parte, per la stessa ragione dei
     * maestri: il blocchetto deve poter dire «si ripete» **mentre lo si guarda**,
     * e la scheda deve poter chiedere «solo questa o tutte?» **prima** di salvare.
     * Un secondo giro per sapere se una prenotazione è di una serie sarebbe un
     * giro fatto dopo che qualcuno ha già premuto.
     */
    serie: serieDellaPrenotazioneSchema.nullable(),
    /** 🌙 La caparra della fascia serale, se ne ha una (25/09/2026): il blocchetto ne mostra lo stato. */
    caparra: caparraInBreveSchema.nullable().optional(),
    /** 🆕 26/09/2026 — chi l'ha organizzata (R12: chi manca lo paga lui). */
    organizerMemberId: z.uuid().nullable().optional(),
    /** 🆕 26/09/2026 — la conferma chiesta all'organizzatore dal bot, l'ultima risposta. */
    conferma: rispostaConfermaSchema.nullable().optional(),
});
/**
 * ⑧ L'importo a carico **si propone dal listino e resta modificabile** (§ 02.2).
 *
 * ⛔ `null` vuol dire **«il listino non lo dice»**, e NON zero. Zero vorrebbe
 * dire «gratis», e una partita segnata gratis non la rivede più nessuno: è la
 * regola non negoziabile n. 9 del piano, e qui è il posto in cui si applica.
 * ⇒ Quando è `null`, `perche` dice perché, e la scheda lo scrive invece di
 *   mostrare uno zero che sembra una risposta.
 */
export const propostaPrezzoSchema = z.object({
    perPlayerCents: z.number().int().nullable(),
    courtCents: z.number().int().nullable(),
    /** Più righe si contendono il primo posto: si è scelto, ma va detto. */
    ambigua: z.boolean(),
    perche: z.string(),
});
/**
 * Una chiusura, **già ritagliata sul giorno**.
 *
 * ⚖️ Il database ne tiene una sola riga anche quando dura tre giorni, e le
 * ricorrenti non hanno nemmeno l'anno. A srotolarle è la query, perché srotolare
 * un «ogni anno» vuol dire sapere quando scatta l'ora legale — e quello lo sa
 * Postgres. Qui arrivano già in pezzi da un giorno, in minuti dalla mezzanotte.
 */
export const chiusuraDelGiornoSchema = z.object({
    id: z.uuid(),
    /** Nullo = tutti i campi. */
    courtId: z.uuid().nullable(),
    date: dataSchema,
    /** 0 = mezzanotte; 1440 = la fine del giorno. */
    fromMinute: z.number().int().min(0).max(1440),
    toMinute: z.number().int().min(0).max(1440),
    reason: z.string().nullable(),
});
/**
 * ⭐ TUTTO IL CALENDARIO IN UNA CHIAMATA SOLA (§ 02.2).
 *
 * Campi, fasce, chiusure e prenotazioni insieme: sono le quattro cose che
 * servono a disegnare la griglia, e chiederle con quattro richieste vorrebbe
 * dire disegnarla quattro volte, ogni volta un po' più giusta.
 */
export const calendarioSchema = z.object({
    from: dataSchema,
    to: dataSchema,
    courts: z.array(campoSchema),
    slots: z.array(fasciaSchema),
    closures: z.array(chiusuraDelGiornoSchema),
    bookings: z.array(prenotazioneSchema),
    /**
     * ⚠️ I MAESTRI ATTIVI STANNO QUI, e non li si chiede alle Impostazioni.
     *
     * ⚖️ `GET /settings/instructors` richiede `manage_settings`: un operatore che
     * ha solo il calendario non ci arriva, e senza l'elenco non potrebbe segnare
     * una lezione. ⇒ L'elenco viaggia con il calendario, che è la schermata che
     * ne ha bisogno, e con il permesso di quella schermata.
     * 📌 *Un dato si chiede con il permesso di ciò per cui serve, non con quello
     *    della schermata dove per caso lo si amministra.*
     */
    instructors: z.array(maestroSchema),
    /**
     * ⏰ L'ORARIO DEL CIRCOLO, e viaggia qui per la stessa ragione dei maestri:
     * è la schermata del calendario ad averne bisogno, e il permesso che la apre
     * non è `manage_settings`.
     *
     * ⭐ Ed è ciò che decide **da che ora a che ora si disegna la griglia**. Fino
     * al 16/09/2026 lo decidevano le fasce, e una giornata senza fasce al mattino
     * cominciava a mezzogiorno: le ore in cui il circolo è aperto ma i soci non
     * prenotano non esistevano affatto (⇒ `circolo.ts`, «L'orario del circolo»).
     */
    hours: orarioCircoloSchema,
});
/**
 * La scheda: la prenotazione più quello che serve a lavorarci sopra.
 *
 * 🖐️ **La scheda si apre piena o non si apre** (§ 03.3): i giocatori arrivano
 * dalla stessa query della prenotazione, quindi o ci sono o la richiesta è
 * fallita. Non esiste il caso «scheda senza roster» che nel gestionale di oggi
 * faceva sembrare vuota una partita piena.
 */
export const schedaPrenotazioneSchema = prenotazioneSchema.extend({
    prezzo: propostaPrezzoSchema,
    /** 🌙 La caparra per esteso, con i gesti che la Segreteria può fare. */
    caparraDettaglio: caparraDellaSegreteriaSchema.nullable().optional(),
    /**
     * 🆕 Fase 3 (R7): un campo fisso della sera senza la sua caparra (nato prima
     * della fase 3): la scheda offre «🌙 Chiedi la caparra del campo fisso».
     */
    caparraDelFissoDaChiedere: z.boolean().optional(),
    /**
     * 🆕 Fase 3 (R6): una lezione o un allenamento della sera, col maestro — lo
     * sono le lezioni su cui si segna «l'allievo non si è presentato» — e la
     * trattenuta già scritta, se c'è.
     */
    lezioneSerale: z.boolean().optional(),
    trattenuta: z
        .object({ id: z.uuid(), amountCents: z.number().int(), reason: z.enum(['disdetta_tardiva', 'no_show']) })
        .nullable()
        .optional(),
    /**
     * 💶 LA CASSA DI QUESTA PARTITA (§ 02.2, Fase 6): gli incassi già registrati e
     * il saldo del borsellino di ciascun giocatore.
     *
     * ⚖️ Viaggia CON la scheda e non si chiede a parte, per la stessa ragione per
     * cui i giocatori arrivano dalla stessa query: il dialogo di incasso deve
     * dire cosa vede **prima** del clic — chi paga, quanto, e se col borsellino
     * quanto resterà. Un saldo chiesto dopo è un saldo che si scopre tardi, e un
     * incasso che scopre lo scoperto dopo il clic è un incasso da stornare.
     */
    cassa: cassaDellaPrenotazioneSchema,
});
// ── Le domande ──────────────────────────────────────────────────────────────
/**
 * 🚨 `coerce`: dalla querystring i numeri arrivano come **testo** — `days=3` è
 * la stringa `"3"`. È lo stesso difetto già pagato sulla prova del prezzo
 * (⇒ `domandaPrezzoSchema`), e si evita qui invece di riscoprirlo dal browser.
 */
export const domandaCalendarioSchema = z.object({
    date: dataSchema,
    /** Mai più di 31 giorni per chiamata (§ 02.2): oltre, è un export, non un calendario. */
    days: z.coerce
        .number('Serve un numero di giorni')
        .int('I giorni si contano interi')
        .min(1, 'Almeno un giorno')
        .max(31, 'Al massimo 31 giorni per volta')
        .default(1),
});
export const domandaDisponibilitaSchema = z.object({
    date: dataSchema,
    /** Nullo = tutti i campi attivi. */
    courtId: z.uuid().nullable().default(null),
    durationMinutes: z.coerce
        .number('Serve una durata in minuti')
        .int('La durata è in minuti interi')
        .min(5, 'Una durata sotto i 5 minuti non è una partita')
        .max(600)
        .default(90),
});
/**
 * ⚠️ IL MINIMO PER METTERE QUALCUNO IN CAMPO, e niente di più.
 *
 * ⚖️ L'anagrafica vera — telefono, email, livello, note, borsellino — è la
 * **Fase 5**, con i suoi permessi (`view_members` e le sue sottosezioni).
 * Questa ricerca esiste perché senza di lei il criterio della Fase 4 non è
 * raggiungibile: *«una giornata vera della segreteria si riesce a impostare
 * interamente»*, e una giornata vera è fatta di soci, non di ospiti.
 * ⇒ Rende **un nome e un identificativo**, che è esattamente ciò che è già
 *   scritto nei blocchi della griglia: chi può vedere il calendario non impara
 *   da qui niente che il calendario non gli mostri già.
 */
export const personaTrovataSchema = z.object({
    id: z.uuid(),
    displayName: z.string(),
});
export const elencoPersoneSchema = z.object({ items: z.array(personaTrovataSchema) });
export const domandaRicercaPersoneSchema = z.object({
    q: z.string().trim().min(2, 'Servono almeno due lettere').max(80),
    limit: z.coerce.number().int().min(1).max(25).default(10),
});
export const slotLiberoSchema = z.object({
    courtId: z.uuid(),
    date: dataSchema,
    time: oraSchema,
    durationMinutes: z.number().int(),
});
export const disponibilitaSchema = z.object({ items: z.array(slotLiberoSchema) });
// ── Le scritture ────────────────────────────────────────────────────────────
/**
 * Un posto in campo, come lo si chiede.
 *
 * ⚠️ O un socio o un nome: **non tutti e due**. Un ospite con anche un
 * `memberId` sarebbe una persona con due identità nella stessa partita, e il
 * giorno in cui si incassa non si saprebbe a chi addebitare.
 */
export const postoInCampoSchema = z
    .object({
    memberId: z.uuid().nullable().default(null),
    guestName: z.string().trim().min(1).max(120).nullable().default(null),
    amountDueCents: z.number().int().min(0).nullable().default(null),
})
    .refine((v) => (v.memberId === null) !== (v.guestName === null), {
    message: 'Serve un socio oppure il nome di un ospite, non tutti e due',
    path: ['guestName'],
});
/**
 * ⭐⭐ **COME SI CHIEDE CHE UNA PRENOTAZIONE SI RIPETA.**
 *
 * ⚠️ `fino` è una **data di fine**, non un numero di volte, ed è una scelta:
 * chi sta al banco sa fin quando va il corso («fino a fine maggio»), non quante
 * volte sono. Contarle a mano per poi scriverne il numero è un conto che può
 * sbagliare, e che il computer sa fare da sé.
 */
export const ripetizioneSchema = z
    .object({
    cadenza: cadenzaSchema,
    /**
     * Solo per la famiglia `settimane`, e vuoto vuol dire «il giorno da cui si
     * parte»: chi prenota il martedì e non spunta niente sta chiedendo i
     * martedì.
     */
    giorni: z.array(z.number().int().min(0).max(6)).max(7).default([]),
    fino: dataSchema,
})
    /*
     * ⛔ Dei giorni della settimana dentro una cadenza che non li srotola non
     * sono un dettaglio da ignorare: chi li manda crede di aver chiesto
     * «lun·mer·ven», e riceverebbe una serie che quei giorni non li guarda
     * nemmeno. 📌 *Un campo ignorato in silenzio è una richiesta che non è mai
     * stata rifiutata e non è mai stata esaudita.*
     */
    .refine((r) => r.giorni.length === 0 || CADENZA[r.cadenza].famiglia === 'settimane', {
    message: 'I giorni della settimana si scelgono solo per le cadenze a settimane',
    path: ['giorni'],
});
export const creaPrenotazioneSchema = z.object({
    courtId: z.uuid('Serve il campo'),
    /** Giorno e ora **di Roma**: a comporre l'istante è Postgres. */
    date: dataSchema,
    time: oraSchema,
    durationMinutes: z.number().int().min(5).max(600),
    type: tipoBookingSchema,
    title: z.string().trim().max(120).nullable().default(null),
    instructorId: z.uuid().nullable().default(null),
    notes: z.string().trim().max(2000).nullable().default(null),
    players: z.array(postoInCampoSchema).max(POSTI_MASSIMI).default([]),
    /**
     * 🌙 Una partita di sera, quando la caparra è accesa: come si risolve (sezione
     * 9 del mockup). Mancando, il gestionale risponde `CAPARRA_DA_SCEGLIERE` e il
     * pannello chiede.
     */
    caparra: caparraDallaSegreteriaSchema.optional(),
});
/**
 * ⭐⭐ **LA MANUTENZIONE SU PIÙ CAMPI IN UNA SCRITTURA SOLA** — 17/09/2026,
 * richiesta sua:
 * 🗣️ *«Quando si seleziona manutenzione mi devi dare la possibilità di
 *   prenotare anche tutti e quattro i campi, quindi avere la possibilità di
 *   prenotarne o uno o due o tre nell'orario predefinito»*.
 *
 * ⚖️ **Perché una rotta a parte e non `courtIds` dentro la creazione normale.**
 * Una prenotazione è **un campo**: chi ci gioca, quanto paga, chi lo tiene. La
 * manutenzione non è gente in campo — è un campo **chiuso** — e chiuderne
 * quattro è **un gesto solo**, non quattro prenotazioni fatte in fila.
 * ⇒ Tenendo `courtId` singolare dove la prenotazione è singolare, quell'invariante
 * resta leggibile; e la risposta può essere un ELENCO senza cambiare forma alla
 * creazione normale, che ne rende una sola.
 *
 * 🔒 **O TUTTI O NESSUNO.** I campi si controllano tutti **prima** di scrivere,
 * e se anche uno solo non è libero non ne nasce nemmeno uno. ⛔ L'alternativa —
 * creare quelli che si può e avvisare degli altri — lascia uno stato a metà che
 * qualcuno deve andare a sistemare, e il messaggio che lo dice è proprio quello
 * che non si legge. 📌 *Una scrittura parziale non è metà lavoro fatto: è
 * lavoro da disfare, e chi lo disfa non sa quale metà.*
 *
 * ⚠️ Niente tipo, niente maestro, niente giocatori: **è per forza una
 * manutenzione**, e la manutenzione non ha nessuna di quelle cose (`CAPIENZA` 0,
 * `VUOLE_MAESTRO` falso). Metterceli darebbe modo di chiedere una cosa che
 * verrebbe comunque buttata via.
 */
export const creaManutenzioneSchema = z.object({
    /**
     * ⚠️ È un ELENCO e non un insieme, perché JSON non ha insiemi: i doppioni li
     * toglie chi la riceve. Chiederne almeno uno serve a non far passare un gesto
     * che non fa niente e sembra riuscito.
     */
    courtIds: z.array(z.uuid('Serve il campo')).min(1, 'Serve almeno un campo'),
    date: dataSchema,
    time: oraSchema,
    durationMinutes: z.number().int().min(5).max(600),
    notes: z.string().trim().max(2000).nullable().default(null),
});
/**
 * ⭐⭐ **UNA SERIE SI CREA DA UNA ROTTA SUA**, come la manutenzione su più campi
 * e per la stessa ragione: la creazione normale rende **una** prenotazione, e
 * questa ne rende molte più l'elenco di quelle che non si sono potute fare.
 * Farle rendere due forme diverse a seconda di un campo del corpo vorrebbe dire
 * che chi la chiama non sa cosa gli tornerà finché non gli torna.
 */
export const creaSerieSchema = creaPrenotazioneSchema
    .extend({
    ripeti: ripetizioneSchema,
    /**
     * 🆕 **Il livello del corso** (voce 19c, 24/09/2026): dice quale quota del
     * «Listino prezzi lezioni» paga ogni iscritto. Solo sul corso, e sul corso
     * è obbligatorio — un corso senza livello non avrebbe una quota da nessuna
     * parte.
     */
    livello: livelloCorsoSchema.nullable().default(null),
})
    .refine((b) => (b.type === 'corso') === (b.livello !== null), {
    message: 'Il livello si sceglie sul corso, e solo sul corso',
    path: ['livello'],
})
    /**
     * 🏷️ **Il corso ha un nome** (voce 10, 27/09/2026). 🗣️ *«da gestionale
     * quando creo un corso mi manca da mettere il nome del corso»*. È la stessa
     * regola dell'area maestro (`aproUnCorsoSchema`): un corso vive mesi e si
     * sceglie da un elenco, e «Corso», «Corso», «Corso» non si distinguono.
     * Le due porte che creano la stessa cosa adesso chiedono la stessa cosa.
     */
    .refine((b) => b.type !== 'corso' || (b.title?.trim().length ?? 0) >= 2, {
    message: 'Dai un nome al corso',
    path: ['title'],
});
/** Un giorno della serie che non si è potuto prenotare, e il perché. */
export const giornoSaltatoSchema = z.object({
    date: dataSchema,
    perche: z.string(),
});
/**
 * ⭐⭐ **UNA SERIE NON È «O TUTTO O NIENTE», e la manutenzione sì: la differenza
 * va capita o si applica la regola sbagliata.**
 *
 * ⚖️ Quattro campi da chiudere alle 14:00 sono **un gesto solo, in un istante
 * solo**: farne tre lascia uno stato a metà che qualcuno deve andare a
 * sistemare. Sei mesi di giovedì invece attraversano **tornei, chiusure e
 * partite già prenotate**: che qualche giovedì sia occupato non è un guasto, è
 * la normalità. Fermare tutta la serie per uno di quelli vorrebbe dire non
 * poterla mai creare — e chi ci prova finirebbe a inserirle a mano una per una,
 * cioè il lavoro che questa cosa doveva togliere.
 * 📌 *«O tutto o niente» protegge un gesto istantaneo; su un gesto che copre
 *    mesi diventa il modo di non fare niente.*
 *
 * 🚨 **Ma niente di saltato è silenzioso**: i giorni che non si sono potuti fare
 * tornano qui **con il motivo**, e la scheda li scrive. Una scrittura parziale
 * si può accettare solo se chi l'ha chiesta sa esattamente quale metà è
 * mancata.
 */
export const serieCreataSchema = z.object({
    serieId: z.uuid(),
    /** La prima nata: è quella che il pannello apre subito dopo. */
    prima: schedaPrenotazioneSchema,
    /** Quante ne sono nate in tutto, la prima compresa. */
    quante: z.number().int(),
    saltate: z.array(giornoSaltatoSchema),
});
/**
 * 🔁⭐⭐ **MODIFICARE UNA PERIODICITÀ = SPOSTARE LA SUA FINE** — 17/09/2026, sua:
 * *«e logicamente di modificare o cancellare una periodicità»*.
 *
 * ⚖️ **Una data sola, e va in tutt'e due i versi**: più in là fa nascere le
 * volte che mancano, più in qua fa sparire quelle che avanzano. Due comandi
 * separati — «allunga» e «accorcia» — sarebbero due bottoni per una domanda
 * sola, e chi guarda dovrebbe sapere *prima* quale dei due gli serve.
 * 📌 *Se la cosa che si cambia è una, il comando è uno: la direzione la dice
 *    il numero, non l'operatore.*
 *
 * ⛔ **La cadenza non si cambia**, ed è dichiarato: passare da settimanale a
 * quindicinale vorrebbe dire cancellare metà delle volte già fatte e
 * ricrearne altre — cioè buttare via prenotazioni che possono avere gente
 * dentro e soldi in cassa. Chi vuole cambiare passo fa finire questa serie e ne
 * comincia un'altra: è lo stesso risultato, con in mezzo un gesto che si vede.
 */
export const modificaSerieSchema = z.object({
    fino: dataSchema,
});
/** Com'è andata a spostare la fine di una serie. */
export const serieAggiornataSchema = z.object({
    /** Quante ne sono nate adesso (zero se la si è accorciata). */
    nate: z.number().int(),
    /** Quante se ne sono tolte (zero se la si è allungata). */
    tolte: z.number().int(),
    /** Quante ne ha in tutto, adesso. */
    quante: z.number().int(),
    saltate: z.array(giornoSaltatoSchema),
});
/** Le manutenzioni nate insieme, nell'ordine dei campi che sono stati scelti. */
export const manutenzioniCreateSchema = z.object({ items: z.array(schedaPrenotazioneSchema) });
/**
 * 🚨 SCRITTO PER ESTESO, come `modificaCampoSchema` e per lo stesso motivo
 * misurato: `creaPrenotazioneSchema.partial()` porterebbe dentro i
 * `.default(null)` della creazione, e una modifica del solo orario
 * **cancellerebbe in silenzio** titolo, maestro e note.
 */
export const modificaPrenotazioneSchema = z
    .object({
    courtId: z.uuid().optional(),
    date: dataSchema.optional(),
    time: oraSchema.optional(),
    durationMinutes: z.number().int().min(5).max(600).optional(),
    type: tipoBookingSchema.optional(),
    title: z.string().trim().max(120).nullable().optional(),
    instructorId: z.uuid().nullable().optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
    /**
     * 🔁 A chi si applica, se questa prenotazione è di una serie. Su una che
     * sta per conto suo non cambia niente: c'è una prenotazione sola.
     */
    ambito: ambitoSerieSchema.default('una'),
})
    .refine((v) => Object.keys(v).filter((k) => k !== 'ambito').length > 0, {
    message: 'Non c’è niente da cambiare',
})
    /**
     * ⛔ **LA DATA NON SI CAMBIA PER TUTTA UNA SERIE**, e il rifiuto è qui invece
     * che nel server perché è una contraddizione nei termini, non una regola del
     * circolo: le occorrenze di una serie sono fatte di giorni **diversi**, e
     * dargliene uno solo le farebbe cadere tutte sullo stesso — cioè le
     * distruggerebbe.
     * ⭐ L'ora, il campo e la durata invece si spostano benissimo: «il corso passa
     *   alle 19:00 da qui in poi» è esattamente la cosa che serve.
     */
    .refine((v) => !(v.ambito !== 'una' && v.date !== undefined), {
    message: 'Il giorno si cambia una prenotazione per volta: una serie è fatta di giorni diversi.',
    path: ['date'],
});
export const annullaPrenotazioneSchema = z.object({
    reason: z.string().trim().max(200).nullable().default(null),
    /** 🔁 Quante della serie se ne vanno: solo questa, da qui in poi, o tutte. */
    ambito: ambitoSerieSchema.default('una'),
    /**
     * 🆕 26/09/2026 (fase 3 della caparra, R6) — **chi la disdice**, per una
     * lezione della sera a meno di 48 ore: `socio` (il maestro o l'allievo ⇒ 30 €
     * dal compenso del maestro) o `circolo` (campo, luci, maltempo ⇒ niente).
     * Assente quando non serve: se serve, il gestionale lo chiede
     * (`CHI_DISDICE_LA_LEZIONE`).
     */
    perChi: z.enum(['socio', 'circolo']).optional(),
});
export const modificaGiocatoreSchema = z
    .object({
    amountDueCents: z.number().int().min(0).optional(),
    position: z.number().int().min(1).max(POSTI_MASSIMI).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Non c’è niente da cambiare' });
//# sourceMappingURL=calendario.js.map